import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { ThemeOptions, createTheme, ThemeProvider } from '@mui/material'
import { jaJP } from '@mui/x-data-grid/locales'
import { colors } from 'shared/colors'

import { Loading } from 'components/controls/loading'
import { TenantResult, GetTenant, GetTenantTheme } from 'interface/tenant'

import { UnknownTenantPage } from 'routes/errorPages/unknownTenantPage'
import { isNotFound } from 'shared/api'
import { ErrorInfo } from 'components/controls/errorInfo'

import * as echarts from 'echarts/core'

const TenantContext = createContext<TenantResult>(undefined as never)
export const useTenant = () => useContext(TenantContext)

declare module '@mui/material/styles' {
  interface Palette {
    careNet: Palette['primary']
    dpc: Palette['secondary']
    cancer: Palette['warning']
    trial: Palette['primary']
    journal: Palette['success']
    senmoni: Palette['info']
    research: Palette['info']
    news: Palette['primary']
    mrp: Palette['secondary']
    live: Palette['info']
    picks: Palette['warning']
  }
  interface PaletteOptions {
    careNet: PaletteOptions['primary']
    dpc: PaletteOptions['secondary']
    cancer: PaletteOptions['warning']
    trial: PaletteOptions['primary']
    journal: PaletteOptions['success']
    senmoni: PaletteOptions['info']
    research: PaletteOptions['info']
    news: PaletteOptions['primary']
    mrp: PaletteOptions['secondary']
    live: PaletteOptions['info']
    picks: PaletteOptions['warning']
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    careNet: true
    dpc: true
    cancer: true
    trial: true
    journal: true
    senmoni: true
    research: true
    news: true
    mrp: true
    live: true
    picks: true
  }
}
declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    careNet: true
    dpc: true
    cancer: true
    trial: true
    journal: true
    senmoni: true
    research: true
    news: true
    mrp: true
    live: true
    picks: true
  }
}
declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    careNet: true
    dpc: true
    cancer: true
    trial: true
    journal: true
    senmoni: true
    research: true
    news: true
    mrp: true
    live: true
    picks: true
  }
}
declare module '@mui/material/LinearProgress' {
  interface LinearProgressPropsColorOverrides {
    careNet: true
    dpc: true
    cancer: true
    trial: true
    journal: true
    senmoni: true
    research: true
    news: true
    mrp: true
    live: true
    picks: true
  }
}
const defaultTheme: ThemeOptions = {
  typography: {
    fontFamily: ['Noto Sans JP', 'sans-serif'].join(','),
    fontSize: 14,
  },
  palette: colors
}

const echartColorTheme = [
  '#5470c6',
  '#91cc75',
  '#fac858',
  '#ee6666',
  '#73c0de',
  '#3ba272',
  '#fc8452',
  '#9a60b4',
  '#ea7ccc'
]

type TenantProviderProps = {
  domain: string
  children: ReactNode
}
export const TenantProvider = ({
  domain,
  children
}: TenantProviderProps) => {
  const navigate = useNavigate()

  const [loading, setLoading] = useState<boolean>(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [error, setError] = useState<any | null>(null)
  const [tenant, setTenant] = useState<TenantResult | null>(null)
  const [theme, setTheme] = useState<ThemeOptions>(createTheme(defaultTheme, jaJP))

  useEffect(
    () => {
      if (tenant) return

      echarts.registerTheme('insite', { color: echartColorTheme })

      setLoading(true)

      Promise.all([
        GetTenant(domain),
        GetTenantTheme(domain)
      ])
      .then(
        ([tenantResponse, tenantTheme]) => {
          if (tenantResponse.domain !== domain) {
            navigate(`${domain}/logoff`, { relative: 'route' })
            return
          }
          setTenant(tenantResponse)
          if (tenantTheme) setTheme(tenantTheme)
        }
      )
      .catch(reason => {
        setError(reason)
      })
      .finally(() => {
        setLoading(false)
      })
    },
    [domain, tenant, navigate]
  )

  return loading ? (
    <Loading />
  ) : error ? (
    isNotFound(error) ? (
      <UnknownTenantPage />
    ) : (
      <ErrorInfo error={error}/>
    )
  ) : tenant ? (
    <TenantContext.Provider value={tenant}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </TenantContext.Provider>
  ) : <></>
}
